import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";

// Champs du modèle Operation renvoyés au backend (les extras sont ignorés côté Pydantic).
const MODEL_FIELDS = [
  "siteId", "donneurOrdre", "eeId", "sousTraitants", "zones", "natureTravaux", "criticite",
  "dateDebut", "dateFin", "titre", "description", "statut", "organisation", "direction",
  "responsableOperation", "fonctionResponsable", "siteOccupe", "presencePublicOuCollaborateurs",
  "entrepriseExterieure", "sousTraitance", "recurrence", "horaires", "facteursCriticite",
  "criticiteJustification", "questionnaire", "documentsObligatoiresPresents", "habilitationsControlees",
  "typeOperation", "identificationEU", "motifsEtablissement", "intervenants", "entrepriseCoordinatrice",
  "descriptionDetail", "phases", "installationsEU", "habilitationsRequises", "secoursDetail",
  "consignesGenerales", "annexes",
];

const EU_FIELDS = [
  ["raisonSociale", "Raison sociale"], ["activite", "Activité"], ["adresse", "Adresse du site"],
  ["siret", "SIRET"], ["representant", "Représentant / donneur d'ordre"],
  ["fonctionRepresentant", "Fonction du représentant"], ["coordinationHSE", "Chargé de coordination sécurité EU"],
  ["contactUrgence", "Contact urgence 24/7"],
];
const DD_FIELDS = [
  ["objet", "Objet de l'opération"], ["perimetre", "Périmètre technique"], ["duree", "Durée (précision)"],
  ["amplitude", "Amplitude chantier"], ["dateEtablissement", "Date d'établissement"],
];
const SECOURS_FIELDS = [
  ["alerteInterne", "Alerte interne"], ["secoursExternes", "Secours externes"],
  ["pointRassemblement", "Point de rassemblement"], ["sst", "Sauveteurs secouristes du travail"],
  ["premiereIntervention", "Première intervention"], ["consigneHommeATerre", "Consigne « homme à terre »"],
  ["evacuationHauteur", "Évacuation en hauteur"],
];
const CONSIGNES_FIELDS = [
  ["epi", "EPI"], ["circulation", "Circulation"], ["interdictions", "Interdictions"], ["environnement", "Environnement"],
];

function Section({ title, children, testid, open, onToggle }) {
  return (
    <div className="card" style={{ marginBottom: 12 }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={onToggle}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <h3 style={{ fontSize: ".98rem", margin: 0 }}>{title}</h3>
      </div>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

export default function PdpComplements({ op, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState("eu");
  const toggle = (k) => setOpenKey((v) => (v === k ? "" : k));
  const [f, setF] = useState({
    identificationEU: { ...(op.identificationEU || {}) },
    descriptionDetail: { ...(op.descriptionDetail || {}) },
    motifsEtablissement: [...(op.motifsEtablissement || [])],
    intervenants: [...(op.intervenants || [])],
    entrepriseCoordinatrice: op.entrepriseCoordinatrice || "",
    phases: [...(op.phases || [])],
    installationsEU: op.installationsEU || "",
    habilitationsRequises: op.habilitationsRequises || "",
    secoursDetail: { ...(op.secoursDetail || {}) },
    consignesGenerales: { ...(op.consignesGenerales || {}) },
    annexes: [...(op.annexes || [])],
  });

  const upObj = (grp, k, v) => setF((s) => ({ ...s, [grp]: { ...s[grp], [k]: v } }));
  const upList = (grp, i, k, v) => setF((s) => { const a = [...s[grp]]; a[i] = { ...a[i], [k]: v }; return { ...s, [grp]: a }; });
  const addRow = (grp, row) => setF((s) => ({ ...s, [grp]: [...s[grp], row] }));
  const rmRow = (grp, i) => setF((s) => ({ ...s, [grp]: s[grp].filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of MODEL_FIELDS) if (op[k] !== undefined) payload[k] = op[k];
      Object.assign(payload, f);
      payload.version = op.version;
      await api.put(`/operations/${op.id}`, payload);
      toast.success("Compléments enregistrés. Régénérez le plan pour les intégrer au PDF.");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pdp-complements">
      <div className="notice">Compléments documentaires du Plan de prévention (gabarit officiel R.4511-1 s.). Les risques d'interférence et la matrice §4.1 restent produits par le moteur de coactivité. Régénérez le plan après enregistrement pour les reporter dans le PDF.</div>

      <Section title="§1 Identification — Entreprise utilisatrice (EU)" testid="cx-eu" open={openKey === "eu"} onToggle={() => toggle("eu")}>
        <div className="row2">
          {EU_FIELDS.map(([k, lbl]) => (
            <div key={k}><label className="f">{lbl}</label>
              <input data-testid={`eu-${k}`} value={f.identificationEU[k] || ""} onChange={(e) => upObj("identificationEU", k, e.target.value)} /></div>
          ))}
        </div>
      </Section>

      <Section title="§1.2 Entreprises extérieures (intervenants)" testid="cx-interv" open={openKey === "interv"} onToggle={() => toggle("interv")}>
        <label className="f">Entreprise coordonnatrice de la coactivité</label>
        <input data-testid="cx-coordinatrice" value={f.entrepriseCoordinatrice} onChange={(e) => setF((s) => ({ ...s, entrepriseCoordinatrice: e.target.value }))} />
        {f.intervenants.map((it, i) => (
          <div key={i} className="row5" style={{ display: "grid", gridTemplateColumns: "60px 1.3fr 1.6fr 60px 1fr 32px", gap: 6, alignItems: "end", marginTop: 8 }}>
            <div><label className="f">Code</label><input value={it.code || ""} onChange={(e) => upList("intervenants", i, "code", e.target.value)} /></div>
            <div><label className="f">Raison sociale</label><input value={it.nom || ""} onChange={(e) => upList("intervenants", i, "nom", e.target.value)} /></div>
            <div><label className="f">Lot / activité</label><input value={it.lot || ""} onChange={(e) => upList("intervenants", i, "lot", e.target.value)} /></div>
            <div><label className="f">Eff.</label><input value={it.effectif || ""} onChange={(e) => upList("intervenants", i, "effectif", e.target.value)} /></div>
            <div><label className="f">Chargé de travaux</label><input value={it.chargeTravaux || ""} onChange={(e) => upList("intervenants", i, "chargeTravaux", e.target.value)} /></div>
            <button className="iconbtn" onClick={() => rmRow("intervenants", i)}><Trash2 size={14} /></button>
          </div>
        ))}
        <button className="btn btn-g btn-sm" style={{ marginTop: 10 }} data-testid="add-interv" onClick={() => addRow("intervenants", { code: `EE${f.intervenants.length + 1}`, nom: "", lot: "", effectif: "", chargeTravaux: "" })}><Plus size={13} />Ajouter une entreprise</button>
      </Section>

      <Section title="§2 Description de l'opération" testid="cx-desc" open={openKey === "desc"} onToggle={() => toggle("desc")}>
        {DD_FIELDS.map(([k, lbl]) => (
          <div key={k}><label className="f">{lbl}</label>
            {k === "objet" || k === "perimetre"
              ? <textarea rows={2} data-testid={`dd-${k}`} value={f.descriptionDetail[k] || ""} onChange={(e) => upObj("descriptionDetail", k, e.target.value)} />
              : <input data-testid={`dd-${k}`} value={f.descriptionDetail[k] || ""} onChange={(e) => upObj("descriptionDetail", k, e.target.value)} />}
          </div>
        ))}
      </Section>

      <Section title="Motif d'établissement écrit (R.4512-7)" testid="cx-motifs" open={openKey === "motifs"} onToggle={() => toggle("motifs")}>
        {f.motifsEtablissement.map((m, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "24px 1.4fr 1.4fr 32px", gap: 6, alignItems: "end", marginTop: 6 }}>
            <input type="checkbox" style={{ width: "auto" }} checked={!!m.coche} onChange={(e) => upList("motifsEtablissement", i, "coche", e.target.checked)} />
            <div><label className="f">Critère</label><input value={m.label || ""} onChange={(e) => upList("motifsEtablissement", i, "label", e.target.value)} /></div>
            <div><label className="f">Élément justificatif</label><input value={m.detail || ""} onChange={(e) => upList("motifsEtablissement", i, "detail", e.target.value)} /></div>
            <button className="iconbtn" onClick={() => rmRow("motifsEtablissement", i)}><Trash2 size={14} /></button>
          </div>
        ))}
        <button className="btn btn-g btn-sm" style={{ marginTop: 10 }} data-testid="add-motif" onClick={() => addRow("motifsEtablissement", { label: "", coche: true, detail: "" })}><Plus size={13} />Ajouter un critère</button>
      </Section>

      <Section title="§2.2 Découpage en phases (référentiel temporel)" testid="cx-phases" open={openKey === "phases"} onToggle={() => toggle("phases")}>
        {f.phases.map((p, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "60px 2fr 1.2fr 1fr 32px", gap: 6, alignItems: "end", marginTop: 8 }}>
            <div><label className="f">Phase</label><input value={p.code || ""} onChange={(e) => upList("phases", i, "code", e.target.value)} /></div>
            <div><label className="f">Intitulé</label><input value={p.intitule || ""} onChange={(e) => upList("phases", i, "intitule", e.target.value)} /></div>
            <div><label className="f">Créneau</label><input value={p.creneau || ""} onChange={(e) => upList("phases", i, "creneau", e.target.value)} /></div>
            <div><label className="f">EE</label><input value={p.ee || ""} onChange={(e) => upList("phases", i, "ee", e.target.value)} /></div>
            <button className="iconbtn" onClick={() => rmRow("phases", i)}><Trash2 size={14} /></button>
          </div>
        ))}
        <button className="btn btn-g btn-sm" style={{ marginTop: 10 }} data-testid="add-phase" onClick={() => addRow("phases", { code: `P${f.phases.length}`, intitule: "", creneau: "", ee: "" })}><Plus size={13} />Ajouter une phase</button>
      </Section>

      <Section title="§5 Consignes générales de sécurité" testid="cx-consignes" open={openKey === "consignes"} onToggle={() => toggle("consignes")}>
        {CONSIGNES_FIELDS.map(([k, lbl]) => (
          <div key={k}><label className="f">{lbl}</label>
            <textarea rows={2} value={f.consignesGenerales[k] || ""} onChange={(e) => upObj("consignesGenerales", k, e.target.value)} placeholder="Laisser vide pour utiliser le texte réglementaire par défaut" /></div>
        ))}
      </Section>

      <Section title="§7 Habilitations & §9 Installations" testid="cx-habinst" open={openKey === "habinst"} onToggle={() => toggle("habinst")}>
        <label className="f">Habilitations, formations et informations requises</label>
        <textarea rows={3} data-testid="cx-habilitations" value={f.habilitationsRequises} onChange={(e) => setF((s) => ({ ...s, habilitationsRequises: e.target.value }))} />
        <label className="f">Installations mises à disposition par l'EU</label>
        <textarea rows={3} data-testid="cx-installations" value={f.installationsEU} onChange={(e) => setF((s) => ({ ...s, installationsEU: e.target.value }))} />
      </Section>

      <Section title="§8 Organisation des secours" testid="cx-secours" open={openKey === "secours"} onToggle={() => toggle("secours")}>
        <div className="row2">
          {SECOURS_FIELDS.map(([k, lbl]) => (
            <div key={k}><label className="f">{lbl}</label>
              <input value={f.secoursDetail[k] || ""} onChange={(e) => upObj("secoursDetail", k, e.target.value)} /></div>
          ))}
        </div>
      </Section>

      <Section title="§12 Annexes" testid="cx-annexes" open={openKey === "annexes"} onToggle={() => toggle("annexes")}>
        <div style={{ fontSize: ".78rem", color: "var(--text-muted)", marginBottom: 6 }}>Cochez « présente » pour attester la disponibilité. Une annexe « obligatoire » bloque la signature tant qu'elle n'est pas présente (permis de feu et consignation sont déjà obligatoires automatiquement selon les travaux).</div>
        {f.annexes.map((a, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 32px", gap: 10, alignItems: "center", marginTop: 6 }}>
            <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: ".76rem", width: "auto" }}>
              <input type="checkbox" style={{ width: "auto" }} data-testid={`annexe-present-${i}`} checked={!!a.present} onChange={(e) => upList("annexes", i, "present", e.target.checked)} />présente</label>
            <input value={a.label || ""} onChange={(e) => upList("annexes", i, "label", e.target.value)} />
            <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: ".76rem", width: "auto" }}>
              <input type="checkbox" style={{ width: "auto" }} data-testid={`annexe-oblig-${i}`} checked={!!a.obligatoire} onChange={(e) => upList("annexes", i, "obligatoire", e.target.checked)} />obligatoire</label>
            <button className="iconbtn" onClick={() => rmRow("annexes", i)}><Trash2 size={14} /></button>
          </div>
        ))}
        <button className="btn btn-g btn-sm" style={{ marginTop: 10 }} data-testid="add-annexe" onClick={() => addRow("annexes", { label: `Annexe ${f.annexes.length + 1} — `, present: false, obligatoire: false })}><Plus size={13} />Ajouter une annexe</button>
      </Section>

      <div style={{ position: "sticky", bottom: 0, padding: "10px 0" }}>
        <button className="btn btn-p" onClick={save} disabled={busy} data-testid="cx-save"><Save size={14} />{busy ? "Enregistrement…" : "Enregistrer les compléments"}</button>
      </div>
    </div>
  );
}
